import "./styles.css";

import { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useScreen } from 'Screen/ScreenContext';
import ChargebackDetails from "./ChargebackDetails";

import { IconBuildingBank } from "@tabler/icons-react";

import BankOnboardingCard from "./components/BankOnboardingCard";
import api from "@helpers/api";
import ChargesHistorySection from "./tabs/Charges";
import SubscriptionsSection from "./tabs/Subscriptions";
import WithdrawalSection from "./tabs/Withdrawals";
import ChargebacksSection from "./tabs/Chargebacks";
import Balance from "./components/Balance";
import TabsSelector from "@components/TabsSelector";

const tabsConfig = [
    {
        key: 'history',
        label: 'Histórico de cobranças',
        screenTitle: 'Cobranças',
        screenUrl: 'banking/charges',
        path: '/banking/charges',
        component: <ChargesHistorySection />,
        showInTabs: true,
        match: (path) => path.includes('/banking/charges')
    },
    {
        key: 'subscriptions',
        label: 'Assinaturas',
        screenTitle: 'Assinaturas',
        screenUrl: 'banking/subscriptions',
        path: '/banking/subscriptions',
        component: <SubscriptionsSection />,
        showInTabs: true,
        match: (path) => path.includes('/banking/subscriptions')
    },
    {
        key: 'withdrawals',
        label: 'Saques',
        screenTitle: 'Saques',
        screenUrl: 'banking/withdrawal',
        path: '/banking/withdrawal',
        component: <WithdrawalSection />,
        showInTabs: true,
        match: (path) => path.includes('/banking/withdrawal'),
    },
    {
        key: 'chargebacks',
        label: 'Chargebacks',
        screenTitle: 'Chargebacks',
        screenUrl: 'banking/chargebacks',
        path: '/banking/chargebacks',
        component: <ChargebacksSection />,
        showInTabs: true,
        match: (path) => path.includes('/banking/chargebacks'),
    },
    {
        key: 'anticipations',
        path: '/banking/anticipations',
        showInTabs: false,
        match: (path) => path.includes('/banking/anticipations')
    },
    {
        key: 'commissions',
        label: 'Comissões',
        disabled: true,
        badge: 'Em breve',
        showInTabs: true
    }
]

export default function Banking() {
    document.title = "Banking | Ginastee";

    const { chargebackId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { setBreadcrumb } = useScreen();

    // Determinar a aba inicial baseada na rota
    const getInitialTab = () => {
        const path = location.pathname;
        const foundTab = tabsConfig.find(tab => tab.match && tab.match(path));
        return foundTab ? foundTab.key : 'history';
    };

    const [tab, setTab] = useState(getInitialTab());

    // Atualizar a aba quando a rota mudar
    useEffect(() => {
        const newTab = getInitialTab();
        if (newTab !== tab) {
            setTab(newTab);
        }
    }, [location.pathname, tab]);

    const memoizedBalance = useMemo(() => {
        return <Balance />;
    }, []); // Array vazio significa que só renderiza uma vez

    const activeTabConfig = tabsConfig.find(t => t.key === tab);

    useEffect(() => {
        if (chargebackId) return;
        const items = [
            { label: 'Banking', href: '/banking', icon: <IconBuildingBank size={18} /> },
            ...(activeTabConfig?.screenTitle
                ? [{ label: activeTabConfig.screenTitle, href: activeTabConfig.path }]
                : []),
        ]
        setBreadcrumb(items)
        return () => setBreadcrumb(null)
    }, [activeTabConfig?.key, chargebackId]);

    // --- Open chargeback count (for tab badge) ---
    const [chargebackOpenCount, setChargebackOpenCount] = useState(0);

    useEffect(() => {
        api.get('charge/chargeback/list?page=1&per_page=1')
            .then(({ data }) => {
                setChargebackOpenCount(data?.summary?.in_progress?.charges ?? 0);
            })
            .catch(() => { });
    }, []);

    // --- Onboarding Logic ---
    const [loadingWallet, setLoadingWallet] = useState(true);
    const [walletStatus, setWalletStatus] = useState(null);

    useEffect(() => {
        fetchWalletStatus();
    }, []);

    async function fetchWalletStatus() {
        try {
            const { data } = await api.get("wallet");

            setWalletStatus(data?.statuses?.general);
        } catch (e) {
            console.error("Failed to load wallet status", e);
        } finally {
            setLoadingWallet(false);
        }
    }

    // Detail page: render ChargebackDetails directly, skipping tabs and balance
    if (chargebackId) {
        return <ChargebackDetails chargebackId={chargebackId} />;
    }

    if (loadingWallet) {
        return null;
    }

    // If status is not approved
    if (walletStatus !== 'approved') {
        return (
            <div className="screen-body">
                <div className="banking-onboarding-wrapper">
                    <BankOnboardingCard />
                </div>
            </div>
        );
    }

    return (
        <div className="banking-screen-content pb-9">
            <div className="banking-header-sticky">
                <TabsSelector
                    tabs={tabsConfig.filter(t => t.showInTabs).map(t => ({
                        key: t.key,
                        label: t.label,
                        disabled: t.disabled,
                        badge: t.badge,
                        countBadge: t.key === 'chargebacks' && chargebackOpenCount > 0 ? chargebackOpenCount : undefined,
                    }))}
                    value={tab}
                    onChange={(newTabKey) => {
                        setTab(newTabKey);
                        const targetTab = tabsConfig.find(t => t.key === newTabKey);
                        if (targetTab && targetTab.path) {
                            navigate(targetTab.path);
                        }
                    }}
                    className="banking-tabs-selector"
                />
            </div>

            {activeTabConfig?.component}
        </div>
    );
}

